import React, { useEffect, useMemo, useState } from "react";
import "./../../../styles/SupportDashboard.css";
import Button from './../../ui/Button';

export default function CreateSiteModal({ isOpen, onClose, sites = [], onSubmit }) {
  const [siteName, setSiteName] = useState("");
  const [parentId, setParentId] = useState("");
  const [parentQuery, setParentQuery] = useState("");

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [err, setErr] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    setSiteName("");
    setParentId("1");
    setParentQuery("");
    setSaving(false);
    setSuccess("");
    setErr({});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const parentOptions = useMemo(() => {
    const normalized = (sites || [])
      .map((s) => ({
        label: s.sitename,
        value: String(s.id),
        level: s.level || "",
      }))
      .filter((s) => s.value !== "1");

    const q = parentQuery.trim().toLowerCase();
    const filtered = !q
      ? normalized
      : normalized.filter((x) =>
          `${x.label} ${x.level} ${x.value}`.toLowerCase().includes(q)
        );

    filtered.sort((a, b) => (a.level?.length || 0) - (b.level?.length || 0));
    return filtered.slice(0, q ? 50 : 200);
  }, [sites, parentQuery]);

  const payloadPreview = useMemo(() => {
    return {
      siteName: siteName.trim(),
      parentId: parentId ? Number(parentId) : 1,
    };
  }, [siteName, parentId]);

  const canSubmit = useMemo(() => {
    return !!siteName.trim() && !!parentId && !saving;
  }, [siteName, parentId, saving]);

  const validate = () => {
  const e = {};
  if (!siteName.trim()) e.siteName = "Site name is required.";
  else if (siteName.trim().length < 3) e.siteName = "Site name looks too short.";
  setErr(e);
  return Object.keys(e).length === 0;
};

  const submit = async () => {
    if (!validate()) return;

    setSaving(true);
    setErr({});
    setSuccess("");

    try {
      await onSubmit({
        siteName: siteName.trim(),
        parentId: Number(parentId || 1),
      });

      setSuccess(`Site created: ${siteName.trim()}`);
      setTimeout(() => onClose(), 1100);
    } catch (ex) {
      const api = ex?.body ?? ex?.response?.data ?? null;
      const errors = Array.isArray(api?.errors) ? api.errors : [];
      const messages = errors.map((e) => e?.message).filter(Boolean);
      const apiMessage =
        messages[0] || api?.message || ex?.message || "Failed to create site.";

      const fieldErrors = {};
      for (const e of errors) {
        const msg = (e?.message || "").toLowerCase();
        if (e?.target) {
          fieldErrors[e.target] = e.message;
          continue;
        }
        if (msg.includes("name")) fieldErrors.siteName = e.message;
        if (msg.includes("parent")) fieldErrors.parentId = e.message;
      }

      setErr((prev) => ({
        ...prev,
        ...fieldErrors,
        api: messages.length ? messages.join(" • ") : apiMessage,
      }));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="sd-modal-backdrop" role="dialog" aria-modal="true">
      <div className="sd-modal sd-modal-md">
        <div className="sd-modal-header">
          <div>
            <h2 className="sd-title">Create site</h2>
            <div className="sd-subtitle">Add a new site under a parent group.</div>
          </div>
          <button className="sd-icon-btn" onClick={onClose} aria-label="Close" type="button">
            ✕
          </button>
        </div>

        <div className="sd-modal-body">
          {err.api && <div className="sd-alert sd-alert-bad">{err.api}</div>}
          {success && <div className="sd-alert sd-alert-good">{success}</div>}

          <div className="sd-field">
            <div className="sd-label">Create site name</div>
            <input
              className={`sd-input ${err.siteName ? "has-error" : ""}`}
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              placeholder='Site name...'
              autoComplete="off"
            />
            {err.siteName && <div className="sd-error">{err.siteName}</div>}
          </div>

          <div className="sd-field">
            <div className="sd-label">Select parent site</div>
            <input
              className={`sd-input ${err.parentId ? "has-error" : ""}`}
              value={parentQuery}
              onChange={(e) => setParentQuery(e.target.value)}
              placeholder="Search parent site…"
              autoComplete="off"
            />

            <div className="sd-dropdown">
              {parentOptions.length === 0 ? (
                <div className="sd-dropdown-empty">No matches</div>
              ) : (
                parentOptions.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    className={`sd-dd-item ${String(parentId) === String(p.value) ? "is-selected" : ""}`}
                    onClick={() => {
                      setParentId(p.value);
                      setParentQuery(p.label);
                    }}
                  >
                    <span className="sd-dd-label">{p.label}</span>
                    <span className="sd-dd-meta">{p.level ? p.level : `#${p.value}`}</span>
                  </button>
                ))
              )}
            </div>

            {err.parentId && <div className="sd-error">{err.parentId}</div>}
            <div className="sd-help">
              Selected parentId: <strong>{parentId || "None"}</strong>
            </div>
          </div>
        </div>

        <div className="sd-modal-footer">
          <Button onClick={onClose} buttonStyle="btn-alternate" disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} buttonStyle="btn-main" disabled={!canSubmit}>
            {saving ? "Creating…" : "Create site"}
          </Button>
        </div>
      </div>
    </div>
  );
}
